<template>
  <div>
    <page-heading :title="$t(`${translationPrefix}.title`)"></page-heading>

    <el-card>
      <div slot="header">
        <el-alert
          :title="alert.title"
          :type="alert.type"
          :description="alert.description"
          show-icon
          :closable="false"
          v-if="alert.title.length > 0">
        </el-alert>
      </div>
      <el-row :gutter="20">
        <el-col :span="6">
          <el-steps :space="100" finish-status="success" align-center direction="vertical" :active="step">
            <el-step title="Account Info" description="This is how the user will authenticate and communicate with the application."></el-step>
            <el-step title="Personal Info" description="This is all the personal related information that is needed to identify this user by other users."></el-step>
            <el-step title="Apps Role & Invite" description="What will this user being doing and what role will they play."></el-step>
            <el-step title="Complete" description="Hooray! You are all done. "></el-step>
          </el-steps>
        </el-col>
        <el-col :span="18">
          <el-carousel arrow="never" ref="carousel" :initial-index="step" :autoplay="false" indicator-position="none" height="500px">
            <el-carousel-item key="1">
              <h3>Account Information</h3>
              <el-form
                ref="createUserForm"
                :model="formUserData"
                :rules="rulesUser"
                :label-width="$root.labelWidth"
                label-position="top"
                v-loading="creating">
                <el-row :gutter="20">
                  <el-col :span="8">
                    <el-form-item :label="$t('modules.user.fields.email.label')" prop="email">
                      <el-input type="email" v-model.lazy.trim="formUserData.email" :placeholder="$t('modules.user.fields.email.placeholder')" :disabled="editMode"></el-input>
                    </el-form-item>
                  </el-col>
                  <el-col :span="8">
                    <el-form-item :label="$t('modules.user.fields.locale.label')" prop="locale">
                      <el-select filterable
                        remote
                        reserve-keyword
                        :placeholder="$t('modules.user.fields.locale.placeholder')" class="full-width" v-model.lazy="formUserData.locale">
                        <el-option v-for="(localeConfig, locale) in locales" :key="locale" :value="locale" :label="localeConfig.name"></el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="8">
                    <el-form-item :label="$t('modules.user.fields.timezone.label')" prop="timezone">
                      <el-select filterable
                        remote
                        reserve-keyword
                        :placeholder="$t('modules.user.fields.timezone.placeholder')" class="full-width" v-model.lazy="formUserData.timezone">
                        <el-option v-for="(timezone, index) in timezones" :key="index" :value="timezone" :label="timezone"></el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item :label="$t('modules.user.fields.password.label')" prop="password">
                      <el-input type="password" v-model.lazy.trim="formUserData.password" :placeholder="$t('modules.user.fields.password.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item :label="$t('modules.user.fields.passwordConfirmation.label')" prop="passwordConfirmation">
                      <el-input type="password" v-model.lazy.trim="formUserData.password_confirmation" :placeholder="$t('modules.user.fields.passwordConfirmation.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item :label="$t('modules.user.fields.admin.label')" prop="admin">
                      <el-switch
                        v-model="formUserData.admin">
                      </el-switch>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item :label="$t('modules.user.fields.master.label')" prop="master">
                      <el-switch
                        v-model="formUserData.master">
                      </el-switch>
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-form>
            </el-carousel-item>

            <el-carousel-item>
              <h3>Profile Information</h3>
              <el-form
                ref="createProfileData"
                :model="formProfileData"
                :rules="rulesProfile"
                :label-width="$root.labelWidth"
                label-position="top"
                v-loading="savingProfile">
                <el-row :gutter="20">
                  <el-col :lg="8" :md="24">
                    <el-form-item :label="$t('modules.profile.fields.firstName.label')" prop="first_name">
                      <el-input type="text" v-model.lazy.trim="formProfileData.first_name" :placeholder="$t('modules.profile.fields.firstName.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                  <el-col :lg="8" :md="24">
                    <el-form-item :label="$t('modules.profile.fields.lastName.label')" prop="last_name">
                      <el-input type="text" v-model.lazy.trim="formProfileData.last_name" :placeholder="$t('modules.profile.fields.lastName.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                  <el-col :lg="8" :md="24">
                    <el-form-item :label="$t('modules.profile.fields.ssn.label')" prop="ssn">
                      <el-input type="number" v-model.lazy.trim="formProfileData.ssn" :placeholder="$t('modules.profile.fields.ssn.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :lg="12" :md="24">
                    <el-form-item :label="$t('modules.profile.fields.address.label')" prop="address">
                      <el-input type="text" v-model.lazy="formProfileData.address" :placeholder="$t('modules.profile.fields.address.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                  <el-col :lg="12" :md="24">
                    <el-form-item :label="$t('modules.profile.fields.address2.label')" prop="address2">
                      <el-input type="text" v-model.lazy="formProfileData.address2" :placeholder="$t('modules.profile.fields.address2.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :lg="12" :md="24">
                    <el-form-item :label="$t('modules.profile.fields.city.label')" prop="city">
                      <el-input type="text" v-model.lazy="formProfileData.city" :placeholder="$t('modules.profile.fields.city.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                  <el-col :lg="6" :md="24">
                    <el-form-item :label="$t('modules.profile.fields.state.label')" prop="state">
                      <el-select
                        v-model="formProfileData.state"
                        filterable
                        remote
                        reserve-keyword
                        :placeholder="$t('modules.profile.fields.state.placeholder')">
                        <el-option
                          v-for="(state, index) in defaults.states"
                          :key="index"
                          :label="state.name"
                          :value="state.name">
                        </el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :lg="6" :md="24">
                    <el-form-item :label="$t('modules.profile.fields.zip.label')" prop="zip">
                      <el-input type="text" v-model.lazy.trim="formProfileData.zip" :placeholder="$t('modules.profile.fields.zip.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :lg="12" :md="24">
                    <el-form-item :label="$t('modules.profile.fields.phone.label')" prop="phone">
                      <el-input type="text" v-model.lazy="formProfileData.phone" :placeholder="$t('modules.profile.fields.phone.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                  <el-col :lg="12" :md="24">
                    <el-form-item :label="$t('modules.profile.fields.title.label')" prop="title">
                      <el-input type="text" v-model.lazy="formProfileData.title" :placeholder="$t('modules.profile.fields.title.placeholder')"></el-input>
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-form>
            </el-carousel-item>

            <el-carousel-item>
              <h3>Apps, Roles & Permissions</h3>
              <el-card v-for="(app, index) in apps" :key="index">
                <div slot="header">
                  {{ app.name }}
                </div>
                <el-row :gutter="20">
                 
                  <el-col :span="24">
                    <el-form
                      ref="sendInvitationForm"
                      :inline="true"
                      :model="formInviteData"
                      :rules="rulesInvite"
                      label-width="150px"
                      label-position="top"
                      v-loading="creating"
                    >    
                      <el-row>
                        <el-col>
                          <el-form-item label="User Role" prop="role_id">
                            <el-select v-model.lazy.trim="formInviteData.role_id" placeholder="Select role">
                              <el-option
                                      v-for="(role, index) in roles"
                                      :key="index"
                                      :label="role.name"
                                      :value="role.id.toString()">

                              </el-option>
                            </el-select>
                          </el-form-item>
                        </el-col>
                      </el-row>
                    </el-form>
                  </el-col>
                </el-row>
              </el-card>
            </el-carousel-item>

            <el-carousel-item>

            </el-carousel-item>

          </el-carousel>
        </el-col>
      </el-row>

      <span slot="footer">
        <el-row :gutter="20" type="flex">
          <el-col :span="12">
            <el-button type="primary" @click="submitStep(-1)" :loading="processing" v-if="step > 0">Go Back</el-button>
          </el-col>
          <el-col :span="12">
            <div align="right">
              <el-button type="primary" @click="submitStep(1)" :loading="processing">Save & Continue</el-button>
            </div>
          </el-col>
        </el-row>
      </span>
    </el-card>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { get } from 'lodash';
  import moment from 'moment';
  import 'moment-timezone';

  import validations from '@/utils/validations';
  import formPopulator from '@/mixins/form_populator';
  import availableLocales from '@/i18n/available_locales';
  import USStates from '@/mixins/states_us';

  export default {
    name: 'UserCreate',
    mixins: [formPopulator, USStates],
    data() {
      return {
        translationPrefix: 'pages.userCreate',
        step: 0,
        alert: {
          title: 'Please fill out the forms completely',
          description: '',
          type: 'info',
        },
        processing: false,
        locales: availableLocales,
        timezones: moment.tz.names(),
        rulesUser: {
          email: [
            { ...validations.email },
            { ...validations.required },
          ],
          locale: [
            { ...validations.required },
          ],
          timezone: [
            { ...validations.required },
          ],
          password: [
            { ...validations.required },
          ],
          password_confirmation: [
            { ...validations.required },
          ],
        },
        rulesProfile: {
          first_name: [
            { ...validations.required },
          ],
          last_name: [
            { ...validations.required },
          ],
        },
        rulesInvite: {
          role_id: [
            { ...validations.required },
          ],
        },
        formUserData: {
          admin: false,
          master: false,
          email: '',
          password: '',
          password_confirmation: '',
          locale: '',
          timezone: '',
        },
        formProfileData: {
          first_name: '',
          last_name: '',
          ssn: '',
          address: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          phone: '',
          title: '',
        },
        formInviteData: {
          role_id: '',
        },
        defaults: {
          countries: [],
          states: [],
        },
      };
    },
    created() {
      // this.fetchRoles();
      this.defaults.states = this.getStates();
    },
    computed: {
      ...mapGetters('entities/apps', ['fetching']),
      ...mapGetters('entities/companies', ['isAppSubscribed']),
      ...mapGetters('entities/profiles', ['creating', 'isUpdating']),
      ...mapGetters('entities/users', ['creating']),

      savingProfile() {
        return this.editMode ? this.isUpdating(this.formProfileData.id) : this.creating;
      },

      roles() {
        return this.$store.getters['entities/roles/query']()
          .get();
      },

      editMode() {
        return get(this, 'formUserData.id') !== undefined;
      },
      apps() {
        return this.$store.getters['entities/apps/query']()
          .get();
      },
      app() {
        return this.apps[0];
      },
      modalTitle() {
        return this.editMode ? 'Edit User' : 'Add New User';
      },
    },
    methods: {
      ...mapActions('entities/apps', ['fetchApps']),
      ...mapActions('entities/profiles', ['createProfile', 'updateProfile']),
      ...mapActions('entities/roles', ['fetchRoles']),
      ...mapActions('entities/users', ['createUser', 'updateUser']),

      isCompanySubscribed(appId) {
        return this.isAppSubscribed(appId);
      },

      handleClickSubmit() {
        this.$refs.form.submitForm();
      },

      moveStep(dir) {
        this.step = this.step + dir;
        this.$refs.carousel.setActiveItem(this.step);
      },

      submitStep(dir) {
        if (dir > -1) {
          switch (this.step) {
            case 0:
              this.$refs.createUserForm.validate((valid) => {
                if (valid) {
                  this.submitUserForm();
                  this.moveStep(dir);
                } else {
                  this.setAlert('error', 'An Error Occurred', this.$t('form.messages.notValid'));
                }
              });
              break;
            case 1:
              this.$refs.createProfileData.validate((valid) => {
                if (valid) {
                  this.submitProfileForm();
                  this.moveStep(dir);
                } else {
                  this.setAlert('error', 'An Error Occurred', this.$t('form.messages.notValid'));
                }
              });
              break;
            case 2:
              this.$refs.sendInvitationForm[0].validate((valid) => {
                if (valid) {
                  this.submitInviteForm();
                  this.moveStep(dir);
                } else {
                  this.setAlert('error', 'An Error Occurred', this.$t('form.messages.notValid'));
                }
              });
              break;
            case 3:
              this.$router.push('/users');
              break;
            default:
              break;
          }
        } else {
          this.moveStep(dir);
        }
      },

      setAlert(type, title, description) {
        this.alert.type = type;
        this.alert.title = title;
        this.alert.description = description;
      },

      submitUserForm() {
        if (this.editMode) {
          this.updateUser({ id: this.formUserData.id, data: this.formUserData })
            .then((response) => {
              if (response.status) {
                this.setAlert('success', 'Update Successful', 'User account has been updated successfully!');
              } else {
                this.setAlert('error', 'An Error Occurred', response.error);
              }
            });
        } else {
          this.createUser(this.formUserData)
            .then((response) => {
              if (response.status) {
                this.formUserData.id = response.data.entity.id;
                this.setAlert('success', 'Create Successful', 'User account has been created successfully!');
              } else {
                this.setAlert('error', 'An Error Occurred', response.error);
              }
            });
        }
      },

      submitProfileForm() {
        if (this.profile) {
          this.updateProfile({ id: this.formProfileData.id, data: this.formProfileData })
            .then((response) => {
              if (response.status) {
                this.setAlert('success', 'Update Successful', this.$t('modules.profile.messages.updated'));
              } else {
                if (this.onError) this.onError(response);
                this.setAlert('error', 'An Error Occurred', response.error);
              }
            });
        } else {
          this.createProfile({ user_id: this.formUserData.id, ...this.formProfileData })
            .then((response) => {
              if (response.status) {
                this.setAlert('success', 'Create Successful', this.$t('modules.profile.messages.created'));
              } else {
                if (this.onError) this.onError(response);
                this.setAlert('error', 'An Error Occurred', response.error);
              }
            });
        }
      },

      submitInviteForm() {
        this.$store.dispatch('entities/userMemberships/inviteUser', { appId: this.app.id, data: { user_id: this.formUserData.id, ...this.formInviteData } })
          .then((response) => {
            if (response.status) {
              this.setAlert('success', 'Create Successful', 'Invitation email has been sent to the user.');
              this.$refs.sendInvitationForm[0].resetFields();
            } else {
              this.setAlert('error', 'An Error Occurred', response.error);
            }
          });
      },

      // populateUserForm() {
      //   const user = this.$store.getters['entities/users/find'](this.formModalContext.userId);
      //   const data = { ...user };
      //   this.populateFormWithModel(data);
      // },

      clearForm() {
        this.$refs.createUserForm.resetFields();
      },

    },
    watch: {
      editMode(newValue) {
        if (newValue) {
          this.populateUserForm();
        }
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/pages/UserCreate.vue